import {Tolgee} from '../integrations/tolgee/index.js'
import {parseDate, type DateValue} from '@ark-ui/react'
import {isToday, isTomorrow, isYesterday, differenceInCalendarDays, startOfDay} from 'date-fns'

export const VERTICALLY_ALIGNED_DOT = '·'

export const getNumberFormatter = (options?: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat(Tolgee.locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  })

export const numberFormatter = new Intl.NumberFormat(Tolgee.locale, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})
export const multipleDigitsNumberFormatter = new Intl.NumberFormat(Tolgee.locale, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 10,
})

export const getDatetimeFormatter = () =>
  new Intl.DateTimeFormat(Tolgee.locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // 24-hour format
  })

export const getDatetimeFormatterUTC = () =>
  new Intl.DateTimeFormat(Tolgee.locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  })

export const getYearMonthFormatter = () =>
  new Intl.DateTimeFormat(Tolgee.locale, {
    year: 'numeric',
    month: '2-digit',
  })

/** For calendar days stored as UTC midnight, such as batch expiries (CY-371). */
export const getYearMonthFormatterUTC = () =>
  new Intl.DateTimeFormat(Tolgee.locale, {
    year: 'numeric',
    month: '2-digit',
    timeZone: 'UTC',
  })

export const getShortDateFormatter = () =>
  new Intl.DateTimeFormat(Tolgee.locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

export const getShortDateFormatterUTC = () =>
  new Intl.DateTimeFormat(Tolgee.locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  })

export const getTimeFormatterUTC = () =>
  new Intl.DateTimeFormat(Tolgee.locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  })

export const getRelativeTimeFormatter = () =>
  new Intl.RelativeTimeFormat(Tolgee.locale, {
    numeric: 'auto',
  })

export const getMonthDayFormatter = () =>
  new Intl.DateTimeFormat(Tolgee.locale, {
    month: 'short',
    day: '2-digit',
  })

export const getMonthDayTimeFormatter = () =>
  new Intl.DateTimeFormat(Tolgee.locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

export const getFullDateFormatter = () =>
  new Intl.DateTimeFormat(Tolgee.locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

export const getExpiryDateTimeFormatter = () =>
  new Intl.DateTimeFormat(Tolgee.locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
export function timestampToDateString(timestamp: number, isSeconds = true): DateValue {
  const date = new Date(isSeconds ? timestamp * 1000 : timestamp)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return parseDate(`${year}-${month}-${day}`)
}

export const formatDateKey = (value: number) => {
  return new Intl.DateTimeFormat(Tolgee.locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(new Date(value))
}

// a birth date of 1 January 1970 is the timestamp 0, so only null/undefined count as missing (CY-314)
export function getAge(timestamp: number): number
export function getAge(timestamp: number | null | undefined): number | undefined
export function getAge(timestamp: number | null | undefined): number | undefined {
  if (!isDefined(timestamp)) return undefined

  const today = new Date()
  const birthDate = new Date(timestamp)

  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  // adjust if the birthday hasn't occurred yet this year
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }

  return age
}

export function getRandomString(length = 8) {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const values = new Uint8Array(length)
  crypto.getRandomValues(values)

  let result = ''
  for (let i = 0; i < length; i++) {
    result += charset[values[i] % charset.length]
  }
  return result
}

export function getNonNullablesArray<T extends {id: string} | undefined>(input: T[]) {
  const output: NonNullable<T>[] = []

  for (const item of input) {
    if (item) output.push(item)
  }

  return output
}

export function getUniquesArray<T extends {id: string} | undefined>(input: T[]) {
  const output: NonNullable<T>[] = []
  const ids = new Set()

  for (const item of input) {
    if (!item || ids.has(item.id)) continue
    output.push(item)
    ids.add(item.id)
  }

  return output
}

export function getFirstElementOrSelf<T extends string>(input: T | T[]) {
  if (Array.isArray(input)) return input[0]
  return input
}

export function elementToArray<T extends string>(input: T | undefined | null) {
  if (!input) return []
  return [input]
}

export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}

export function removeElementAt<T>(items: T[], index: number) {
  if (index < 0 || index >= items.length) return items
  return [...items.slice(0, index), ...items.slice(index + 1)]
}

export function titleCase(input: string) {
  if (!input) return ''
  return input.charAt(0).toUpperCase() + input.slice(1)
}

export function toPascalCase(input: string | null | undefined): string {
  if (!isDefined(input)) return ''

  return input
    .split('.')
    .map(segment => {
      const cleaned = segment
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .trim()

      if (!cleaned) return ''

      return cleaned
        .split(/\s+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    })
    .filter(Boolean)
    .join(' ')
}

function toDate(date: Date | number) {
  return date instanceof Date ? date : new Date(date)
}

export function extractTimestamp(date: Date | number) {
  const _date = toDate(date)
  return _date.toISOString().split('T')[0]
}

export function getDaysDiffBetwenDates(start: Date | number, end: Date | number) {
  const _start = toDate(start)
  const _end = toDate(end)

  const diffInTime = _end.getTime() - _start.getTime()
  const diffInDays = Math.round(diffInTime / (1000 * 60 * 60 * 24))

  return diffInDays
}

export function getCalendarDaysDiffBetweenDates(start: Date | number, end: Date | number) {
  return differenceInCalendarDays(startOfDay(end), startOfDay(start))
}

/**
 * Merges two objects of the same shape, ignoring 'undefined' values in the source object.
 */
export const mergeDefined = <T extends Record<string, unknown>>(target: Partial<T>, source: T): T => {
  return (Object.entries(source) as [keyof T, T[keyof T]][]).reduce(
    (acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value
      }
      return acc
    },
    {...target} as T // Cast the starting point to the full Type
  )
}

/**
 * Returns the next possible event types from an xstate v5 machine snapshot.
 * This is the replacement for the removed `state.nextEvents` property.
 */
export function getActorNextEvents(snapshot: {_nodes: Array<{ownEvents: string[]}>}): Set<string> {
  return new Set(snapshot._nodes.flatMap(sn => sn.ownEvents))
}

export function flattenValue(value: unknown, prefix = ''): {path: string; value: unknown}[] {
  if (isNestedValue(value)) {
    return Object.entries(value).flatMap(([key, val]) => flattenValue(val, prefix ? `${prefix}.${key}` : key))
  }

  return [{path: prefix, value}]
}

export function isNestedValue(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

export function parseDateFilterText(value: string[]) {
  return value
    .map(val => {
      const date = new Date(val)
      if (!date.getTime()) return null // date is invalid

      if (isToday(val)) return 'today'
      if (isYesterday(val)) return 'yesterday'
      if (isTomorrow(val)) return 'tomorrow'

      return getShortDateFormatter().format(new Date(val))
    })
    .filter(Boolean) as string[]
}

// joins truthy labels with a middot separator, for one-line summaries
export function joinLabels(
  labels: readonly (string | null | undefined)[] | undefined,
  separator = ` ${VERTICALLY_ALIGNED_DOT} `
) {
  if (!labels) return '-'
  return labels.filter(Boolean).join(separator)
}

export {withUnitSuffix} from './unit.js'
